import { useInfiniteQuery as useOriginalInfiniteQuery } from '@tanstack/react-query'

export interface IWithContinuationToken {
  continuationToken?: string
}
interface IQueryFnParams extends Record<string, unknown> {
  continuationToken: string
  limit: number
}
type QueryFn<
  TQueryData extends IWithContinuationToken,
  TQueryFnParams extends IQueryFnParams,
> = (params: TQueryFnParams) => Promise<TQueryData[]>
type Props<
  TQueryData extends IWithContinuationToken,
  TQueryFnParams extends IQueryFnParams,
> = {
  queryKey: Array<unknown> | ReadonlyArray<unknown>
  queryFn: QueryFn<TQueryData, TQueryFnParams>
  limit: number
  select?: (data: TQueryData[]) => TQueryData[]
  enabled?: boolean
}

export const useConvenientInfiniteQuery = <
  TQueryData extends IWithContinuationToken,
  TQueryFnParams extends IQueryFnParams,
>(
  opts: Props<TQueryData, TQueryFnParams>,
) => {
  return useOriginalInfiniteQuery<
    { data: TQueryData[]; continuationToken: string | undefined },
    Error,
    TQueryData[]
  >({
    queryKey: opts.queryKey,
    enabled: opts.enabled,
    queryFn: async ({ pageParam }) => {
      const results = await opts.queryFn({
        continuationToken: pageParam,
        limit: opts.limit,
      } as TQueryFnParams)

      const hasMore =
        results.length !== undefined && results.length === opts.limit
      return {
        data: results,
        continuationToken: hasMore
          ? results.at(-1)?.continuationToken
          : undefined,
      }
    },
    initialPageParam: '',
    getNextPageParam: lastPage => lastPage.continuationToken,
    select: data => {
      const flattened = data.pages.flatMap(p => p.data)
      return opts.select ? opts.select(flattened) : flattened
    },
  })
}
